import mathProverImg from '../assets/images/math-prover-multi-agent.png';

/**
 * 站内文章详情数据。
 * 每篇文章以 id 为 key，content 为结构化 block 数组。
 * block 类型：paragraph / heading / image / bold-paragraph / cta
 */
const ARTICLES = {
    'math-prover-multi-agent': {
        title: '研发笔记｜数学Prover多智能体协作证明框架',
        tag: '数学智能',
        date: '2026-07-28',
        content: [
            {
                type: 'paragraph',
                text: '数学推理，长期被视为检验AI复杂认知能力的"试金石"。正因其难度足够"硬核"，数学推理既是学术研究的重要试验场，也是通往高复杂度产业智能的必经之路，它高度浓缩了高价值产业场景中的共性挑战。',
            },
            {
                type: 'paragraph',
                text: '近期 IQuest 研发团队受国际数学奥林匹克竞赛（IMO）官方邀请，参与赛题AI解题官方测试，在IMO2026赛题评测中达到金牌线，跻身全球AI数学推理的第一梯队。我们选择的解题方式依靠一套植根于通用推理范式的底层架构，通过模拟"人类数学家团队"的协作方式来工作。',
            },
            { type: 'heading', text: '核心架构' },
            {
                type: 'image',
                src: mathProverImg,
                alt: '数学 Prover 的 Multi-Agent 流程图',
            },
            {
                type: 'paragraph',
                text: '这套"数学Prover多智能体协作证明框架"的核心，是一群各司其职的智能体，按照特定的协作协议"并行化"工作。框架中包含两个角色：Sketcher 与 Prover。',
            },
            {
                type: 'bold-paragraph',
                bold: 'Sketcher：',
                text: '工作流中的核心推理智能体。它在自然语言推理与 Lean 形式化推理之间交替工作，对原始问题进行分解，并提出可能有助于完成证明的候选引理。Sketcher 在提出这些引理时不要求立即给出完整证明，从而能够更自由地探索不同的证明思路、辅助构造和中间结论。',
            },
            {
                type: 'bold-paragraph',
                bold: 'Prover：',
                text: '负责对 Sketcher 提出的候选引理进行形式化检验。它使用 Lean 尝试证明相应引理；当引理不成立时，则尝试发现类型错误、证明失败原因或构造反例。Prover 的验证结果会被反馈给 Sketcher，使其能够修正错误猜想、调整证明方向，并基于已经验证的引理继续展开推理。',
            },
            {
                type: 'paragraph',
                text: '这个"提出—验证—修正"的闭环，在每个智能体对内高频迭代，使得试探的方向始终被严格的反馈信号牵引，不至于在盲目猜测中漂移。',
            },
            {
                type: 'paragraph',
                text: '自然语言推理负责扩大搜索空间，Lean负责检验和收敛路径，二者共同形成可持续纠错的证明流程。',
            },
            {
                type: 'bold-paragraph',
                bold: '共享记忆库（Shared Memory）：',
                text: '如果只有单个Pair的智能体在工作，这不过是一个"猜了又验、验了又猜"的局面。因此，系统会并行运行多个 Sketcher–Prover 实例，从而探索不同的证明路径。同时，我们为所有并行的智能体对配置了一个共享记忆库（Shared Memory）。',
            },
            {
                type: 'paragraph',
                text: '记忆库中存储了"经验资产"，包括已验证引理、失败的证明尝试、反例、有效策略以及中间证明片段。当一个Sketcher需要生成新的候选时，它不仅可以依赖自身的历史经验，还可以主动检索共享记忆中其他Pair在与当前子问题相似的上下文中的有效做法。通过并行探索、形式化验证和跨智能体信息共享，系统逐步收敛到完整且可由 Lean 检验的最终证明。共享记忆就如同一块持续更新的"公共黑板"，让不同智能体能够复用彼此的有效结论，减少重复搜索。这极大地规避了多个智能体在相同路径上重复消耗，使整个系统呈现出"群体智慧"的涌现效应。',
            },
            { type: 'heading', text: '底层创新' },
            {
                type: 'paragraph',
                text: 'Prover模型脱胎于IQuest的自研基模，在高质量的数学数据上进行持续训练。MidTrain 阶段将模型能力集中于数学与形式化推理，语料覆盖从非形式化到形式化、从单轮到交互式的推理数据谱系，并辅以通用指令与 code-agent 数据以缓解灾难性遗忘。',
            },
            {
                type: 'paragraph',
                text: '在此基础上，我们进一步构建面向多智能体工作流的冷启动 SFT 数据，从正确性、格式与交互一致性等维度筛选轨迹，剔除篡改题目、冗余工具调用与幻觉等有害行为，并强化那些真正推进证明目标的关键轮次，使模型在面向具体任务对齐之前即具备可靠的形式化推理基础。同时，为了提升上述数学证明工作流的能力，我们采用多任务、多阶段强化学习框架，分别训练自然语言验证、形式化证明、自然语言证明和引理构造等不同能力。强化学习阶段得到的并不是一个单一模型，而是一组包括自然语言Verifier、Lean Prover、自然语言Prover、Sketcher在内的多个专家模型，以及由这些模型协作形成的高质量数学证明工作流，最终实现独立执行完整的数学证明。',
            },
            {
                type: 'paragraph',
                text: '推理性能上，对于一些需要在有限时间内完成的任务，我们采用训练 DFlash Model 进行投机解码，保证没有精度损失的前提下，有效提升推理速度2倍以上。模型在 minif2f-test 上，取得了 243/244(99.6%) 的结果。',
            },
            { type: 'heading', text: '写在最后' },
            {
                type: 'paragraph',
                text: '我们在实践过程中认识到，AI数学能力要从单次证明突破走向稳定、可扩展的研究能力，必须进一步深入基础模型、训练方法和推理架构。如今，IQuest逐步形成了从自研基模、数学训练、专家模型，到多智能体协作、形式化验证和推理加速的完整技术链路。AI for Math一方面服务于数学研究，帮助完成猜想生成、证明搜索与结果验证；另一方面也为AGI提供可严格检验的推理训练场，持续触碰机器智能的能力上限。更长远看，这还是一场碳基和硅基的互补实验，这种协作将发挥AI和人类各自的优势，最终在复杂问题的求解中，真正实现互相成就。',
            },
            {
                type: 'paragraph',
                text: '未来，IQuest将持续探索，同时将底层算法能力迁移到数学之外领域，推动科学与产业的原始创新。',
            },
            {
                type: 'cta',
                text: '如果你也渴望亲手构建这一切，那么欢迎加入我们，一起写下更多"真实价值的证明"。',
            },
        ],
    },
};

export function getArticle(id) {
    return ARTICLES[id] ?? null;
}

export default ARTICLES;
